import {
  FormSchema,
  HandoverFormSchema,
  HandoverProductSchema,
  ProductSchema,
} from "@/lib/form/schema"
import { FormData, FormParamStatus } from "@/lib/form/types"
import {
  OfflineIpqcBundle,
  OfflineIpqcSession,
} from "@/ipqc/lib/offline-store"
import { EnumsOf } from "@/lib/utils"
interface ShiftProduct {
  id: string
  name: string
}
import { StateCreator } from "@/store/utils"

export const ShiftDialog = {
  None: "None",
  AddProducts: "AddProducts",
} as const

export type ShiftDialog = EnumsOf<typeof ShiftDialog>

export const SessionStatus = {
  Active: "ACTIVE",
  Inactive: "INACTIVE",
  Ended: "ENDED",
} as const
export type SessionStatus = EnumsOf<typeof SessionStatus>

export const initialState: () => IPQCState = () => ({
  schemas: {
    self: {
      products: [],
    },
    handovers: [],
  },
  forms: {
    self: {
      products: [],
    },
    handovers: [],
  },
  histories: {},
  formStatus: {
    self: FormParamStatus.PENDING,
    handovers: {},
  },
  fieldMeta: {
    self: {},
    handovers: [],
  },
  filled: {
    self: {},
    handovers: [],
  },
  shift: {
    id: "",
    processId: "",
    shiftId: "",
    products: [],
    currDialog: ShiftDialog.None,
  },
  currLineIdx: 0,
  currProductIdx: 0,
  currHandoverSessionId: "",
  currHandoverIdx: -1,
  lineStatus: {},
  submitLoading: false,
  isReadOnly: false,
  offline: {
    bundle: null,
    activeSession: null,
    hydrated: false,
  },
})

export interface IPQCState {
  schemas: {
    self: FormSchema
    handovers: HandoverFormSchema[]
  }
  histories: Record<string, ProductSchema>
  forms: {
    self: FormData
    handovers: FormData[]
  }
  formStatus: {
    self: FormParamStatus
    handovers: Record<string, FormParamStatus>
  }
  filled: {
    self: Record<string, boolean>
    handovers: {
      sessionId: string
      filled: Record<string, boolean>
    }[]
  }
  shift: {
    id: string
    processId: string
    shiftId: string
    products: ShiftProduct[]
    currDialog: ShiftDialog
  }
  currHandoverSessionId: string
  currHandoverIdx: number
  currLineIdx: number
  currProductIdx: number
  lineStatus: Record<string, FormParamStatus | undefined>
  submitLoading: boolean
  isReadOnly: boolean
  offline: {
    bundle: OfflineIpqcBundle | null
    activeSession: OfflineIpqcSession["meta"] | null
    hydrated: boolean
  }
}

export type IPQCSlice = {
  ipqc: IPQCState & {
    setSelfSchema(schema: FormSchema): void
    setSelfProductSchema(schema: ProductSchema): void
    addSelfProductSchemas(schemas: ProductSchema[]): void
    removeSelfProductionLine(params: {
      productIdx: number
      lineIdx: number
    }): void
    addProductHistory(schema: ProductSchema): void
    getProductHistory(productId: string): ProductSchema | undefined

    setHandoverSchema(schema: HandoverFormSchema): void
    setHandoverSchemas(schemas: HandoverFormSchema[]): void
    setHandoverProductSchema(
      schema: HandoverProductSchema,
      sessionId: string
    ): void
    setHandoverFormData(data: FormData, sessionId: string): void
    removeHandoverSchema(sessionId: string): void
    removeHandoverProductSchema(schemaId: string, sessionId: string): void

    setShiftDetails(details: Partial<IPQCState["shift"]>): void
    clearShift(): void
    clearFormData(): void
    setShiftDialog(dialog: ShiftDialog): void

    setSelfFormData(data: FormData): void
    setCurrLineIdx(idx: number): void
    setCurrProductIdx(idx: number): void
    setCurrHandoverIdx(idx: number): void
    currLineId(): string
    currProdId(): string

    setLineStatus(params: {
      productId: string
      lineId: string
      status: FormParamStatus | undefined
      sessionId?: string
    }): void
    setLineStatuses(statuses: Record<string, FormParamStatus | undefined>): void

    getLineStatus(params: LineStatusKeyParams | string): FormParamStatus
    getCurrLineStatus(): FormParamStatus
    resetLineStatus(
      statuses?: Record<string, FormParamStatus | undefined>
    ): void
    setSubmitLoading(status: boolean): void

    setSelfFormStatus(status: FormParamStatus): void
    setHandoverFormStatus(sessionId: string, status: FormParamStatus): void

    setSelfFilled(filled: Record<string, boolean>): void
    setHandoversFilled(data: IPQCState["filled"]["handovers"]): void

    removePendingSheets(): void
    setReadOnlyMode(isReadOnly: boolean): void
    setOfflineBundle(bundle: OfflineIpqcBundle | null): void
    setOfflineHydrated(hydrated: boolean): void
    startOfflineSession(meta: OfflineIpqcSession["meta"]): void
    updateOfflineSessionMeta(
      meta: Partial<OfflineIpqcSession["meta"]> | null
    ): void
    hydrateOfflineSession(session: OfflineIpqcSession): void
    clearOfflineSession(): void
  }
}

function actionName(action: string) {
  return `ipqc/${action}`
}

const createIPQCSlice: StateCreator<IPQCSlice> = (set, get) => ({
  ipqc: {
    ...initialState(),
    setSelfSchema(schema) {
      set(
        (state) => {
          state.ipqc.schemas.self = schema

          return state
        },
        undefined,
        actionName("setSelfSchema")
      )
    },
    setSelfProductSchema(schema) {
      set(
        (state) => {
          const foundIdx = state.ipqc.schemas.self.products.findIndex(
            (product) => product.id === schema.id
          )

          if (foundIdx < 0) {
            state.ipqc.schemas.self.products.push(schema)
          } else {
            state.ipqc.schemas.self.products[foundIdx] = schema
          }

          return state
        },
        undefined,
        actionName("setSelfProductSchema")
      )
    },
    addProductHistory(schema) {
      set(
        (state) => {
          state.ipqc.histories[schema.id] = schema

          return state
        },
        undefined,
        actionName("addProductHistory")
      )
    },
    getProductHistory(productId) {
      const state = get().ipqc

      return state.histories[productId]
    },
    addSelfProductSchemas(schemas) {
      set(
        (state) => {
          state.ipqc.schemas.self.products.push(...schemas)

          return state
        },
        undefined,
        actionName("setSelfProductSchemas")
      )
    },
    removeSelfProductionLine(params) {
      set(
        (state) => {
          if (params.lineIdx < 0 || params.productIdx < 0) {
            return state
          }

          const ipqc = state.ipqc
          const self = state.ipqc.schemas.self
          const product = self.products[params.productIdx]
          const forms = ipqc.forms.self
          const line = product.productionLines[params.lineIdx]

          // Delete the line status by ID (no re-indexing needed)
          const lineStatusKey = toLineStatusKey({
            productId: product.id,
            lineId: line.id,
            sessionId: "self",
          })
          delete state.ipqc.lineStatus[lineStatusKey]

          if (product.productionLines.length < 2) {
            // Removing the last line means removing the entire product
            self.products.splice(params.productIdx, 1)
            forms.products.splice(params.productIdx, 1)

            if (params.productIdx !== 0) {
              ipqc.currProductIdx = params.productIdx - 1
            }

            ipqc.currLineIdx = 0
          } else {
            product.productionLines.splice(params.lineIdx, 1)
            forms.products[params.productIdx].productionLines.splice(
              params.lineIdx,
              1
            )

            if (ipqc.currLineIdx > 0) {
              ipqc.currLineIdx -= 1
            }
          }

          return state
        },
        undefined,
        actionName("removeSelfProductionLine")
      )
    },

    setHandoverSchema(schema) {
      set(
        (state) => {
          const foundIdx = state.ipqc.schemas.handovers.findIndex(
            (handover) =>
              handover.handoverFromEmployeeSessionId ===
              schema.handoverFromEmployeeSessionId
          )

          if (foundIdx < 0) {
            state.ipqc.schemas.handovers.push(schema)
            state.ipqc.forms.handovers.push({
              handoverFromEmployeeCode: schema.handoverFromEmployeeCode,
              handoverFromEmployeeSessionId:
                schema.handoverFromEmployeeSessionId,
              products: [],
            })
          } else {
            state.ipqc.schemas.handovers[foundIdx] = schema
          }

          return state
        },
        undefined,
        actionName("setHandoverSchema")
      )
    },
    setHandoverSchemas(schemas) {
      set(
        (state) => {
          state.ipqc.schemas.handovers = schemas
          state.ipqc.forms.handovers = schemas.map((schema) => {
            return {
              handoverFromEmployeeSessionId:
                schema.handoverFromEmployeeSessionId,
              handoverFromEmployeeCode: schema.handoverFromEmployeeCode,
              products: [],
            }
          })
          state.ipqc.filled.handovers = schemas.map((schema) => {
            return {
              sessionId: schema.handoverFromEmployeeSessionId,
              filled: {},
            }
          })

          return state
        },
        undefined,
        actionName("setHandoverSchemas")
      )
    },
    removeHandoverSchema(sessionId) {
      set(
        (state) => {
          state.ipqc.schemas.handovers = state.ipqc.schemas.handovers.filter(
            (handover) => {
              return handover.handoverFromEmployeeSessionId !== sessionId
            }
          )

          return state
        },
        undefined,
        actionName("removeHandoverSchema")
      )
    },
    setHandoverProductSchema(schema, sessionId) {
      set(
        (state) => {
          let schemaFound: ProductSchema | null = null

          const handoverIdx = state.ipqc.schemas.handovers.findIndex(
            (handover) => handover.handoverFromEmployeeSessionId === sessionId
          )

          if (handoverIdx < 0) {
            return state
          }

          const handover = state.ipqc.schemas.handovers[handoverIdx]
          const schemaIdx = handover.products.findIndex(
            (product) => product.id === schema.id
          )

          if (schemaIdx > -1) {
            schemaFound = handover.products[schemaIdx]
          }

          if (schemaFound) {
            state.ipqc.schemas.handovers[handoverIdx].products[schemaIdx] =
              schema
          } else {
            state.ipqc.schemas.handovers[handoverIdx].products.push(schema)
          }

          return state
        },
        undefined,
        actionName("setHandoverProductSchema")
      )
    },
    removeHandoverProductSchema(schemaId, sessionId) {
      set(
        (state) => {
          const handoverIdx = state.ipqc.schemas.handovers.findIndex(
            (handover) => handover.handoverFromEmployeeSessionId === sessionId
          )

          if (handoverIdx < 0) {
            return state
          }

          const handover = state.ipqc.schemas.handovers[handoverIdx]
          handover.products = handover.products.filter(
            (product) => product.id !== schemaId
          )

          return state
        },
        undefined,
        actionName("removeHandoverProductSchema")
      )
    },
    setHandoverFormData(data, sessionId) {
      set(
        (state) => {
          const foundIndex = state.ipqc.forms.handovers.findIndex(
            (handover) => {
              return handover.handoverFromEmployeeSessionId === sessionId
            }
          )

          if (foundIndex < 0) {
            state.ipqc.forms.handovers.push(data)

            return state
          }

          state.ipqc.forms.handovers = state.ipqc.forms.handovers.map(
            (handover) => {
              if (handover.handoverFromEmployeeSessionId === sessionId) {
                return {
                  ...handover,
                  ...data,
                }
              }

              return handover
            }
          )

          return state
        },
        undefined,
        actionName("setHandoverFormData")
      )
    },

    setShiftDetails(details) {
      set(
        (state) => {
          state.ipqc.shift = {
            ...state.ipqc.shift,
            ...details,
          }

          return state
        },

        undefined,
        actionName("setShiftDetails")
      )
    },
    clearShift() {
      set(
        (state) => {
          const bundle = state.ipqc.offline.bundle
          const hydrated = state.ipqc.offline.hydrated
          state.session.session.processes =
            state.session.session.processes.filter(
              (proc) => proc.processId !== state.ipqc.shift.processId
            )

          state.ipqc = {
            ...state.ipqc,
            ...initialState(),
            offline: {
              bundle,
              activeSession: null,
              hydrated,
            },
          }

          return state
        },
        undefined,
        actionName("clearShift")
      )
    },
    clearFormData() {
      set(
        (state) => {
          const initial = initialState()
          state.ipqc.schemas = initial.schemas
          state.ipqc.forms = initial.forms
          state.ipqc.lineStatus = {}
          state.ipqc.histories = {}
          state.ipqc.filled = initial.filled
          state.ipqc.currLineIdx = 0
          state.ipqc.currProductIdx = 0
          state.ipqc.currHandoverIdx = -1
          state.ipqc.currHandoverSessionId = ""

          return state
        },
        undefined,
        actionName("clearFormData")
      )
    },
    setShiftDialog(dialog) {
      set(
        (state) => {
          state.ipqc.shift.currDialog = dialog

          return state
        },
        undefined,
        actionName("setShiftDialog")
      )
    },

    setSelfFormData(data) {
      set(
        (state) => {
          state.ipqc.forms.self = data

          return state
        },
        undefined,
        actionName("setSelfFormData")
      )
    },
    setLineStatus({ status, ...params }) {
      set(
        (state) => {
          state.ipqc.lineStatus[toLineStatusKey(params)] = status

          return state
        },
        undefined,
        actionName("setLineStatus")
      )
    },
    setLineStatuses(statuses) {
      set(
        (state) => {
          state.ipqc.lineStatus = statuses

          return state
        },
        undefined,
        actionName("setLineStatuses")
      )
    },
    getLineStatus(params) {
      const store = get()

      return (
        store.ipqc.lineStatus[
        typeof params === "string" ? params : toLineStatusKey(params)
        ] || FormParamStatus.PENDING
      )
    },
    getCurrLineStatus() {
      const store = get()

      const status =
        store.ipqc.lineStatus[
        toLineStatusKey({
          productId: this.currProdId(),
          lineId: this.currLineId(),
          sessionId: store.ipqc.currHandoverSessionId || "self",
        })
        ]

      return status || FormParamStatus.PENDING
    },
    resetLineStatus(statuses) {
      set(
        (state) => {
          state.ipqc.lineStatus = statuses || {}

          return state
        },
        undefined,
        actionName("resetLineStatus")
      )
    },
    setCurrLineIdx(idx) {
      set(
        (state) => {
          state.ipqc.currLineIdx = idx

          return state
        },
        undefined,
        actionName("setCurrLineIdx")
      )
    },
    currLineId() {
      const state = get()

      if (state.ipqc.currLineIdx < 0 || state.ipqc.currProductIdx < 0) {
        return ""
      }

      if (state.ipqc.currHandoverSessionId) {
        if (
          !state.ipqc.schemas.handovers.length ||
          !state.ipqc.schemas.handovers.at(state.ipqc.currHandoverIdx)?.products
            .length ||
          !state.ipqc.schemas.handovers
            .at(state.ipqc.currHandoverIdx)
            ?.products.at(state.ipqc.currProductIdx)?.productionLines.length
        ) {
          return ""
        }

        return (
          state.ipqc.schemas.handovers
            .at(state.ipqc.currHandoverIdx)
            ?.products.at(state.ipqc.currProductIdx)
            ?.productionLines.at(state.ipqc.currLineIdx)?.id || ""
        )
      }

      if (
        !state.ipqc.schemas.self.products.length ||
        !state.ipqc.schemas.self.products.at(state.ipqc.currProductIdx)
          ?.productionLines.length
      ) {
        return ""
      }

      return (
        state.ipqc.schemas.self.products
          .at(state.ipqc.currProductIdx)
          ?.productionLines.at(state.ipqc.currLineIdx)?.id || ""
      )
    },
    currProdId() {
      const state = get()

      if (state.ipqc.currLineIdx < 0 || state.ipqc.currProductIdx < 0) {
        return ""
      }

      if (state.ipqc.currHandoverSessionId) {
        if (
          !state.ipqc.schemas.handovers.length ||
          !state.ipqc.schemas.handovers.at(state.ipqc.currHandoverIdx)?.products
            .length
        ) {
          return ""
        }

        return (
          state.ipqc.schemas.handovers
            .at(state.ipqc.currHandoverIdx)
            ?.products.at(state.ipqc.currProductIdx)?.id || ""
        )
      }

      if (!state.ipqc.schemas.self.products.length) {
        return ""
      }

      return (
        state.ipqc.schemas.self.products.at(state.ipqc.currProductIdx)?.id || ""
      )
    },
    setCurrProductIdx(idx) {
      set(
        (state) => {
          state.ipqc.currProductIdx = idx

          return state
        },
        undefined,
        actionName("setCurrProductIdx")
      )
    },

    setSubmitLoading(status) {
      set(
        (state) => {
          state.ipqc.submitLoading = status

          return state
        },
        undefined,
        actionName("setSubmitLoading")
      )
    },
    setCurrHandoverIdx(idx) {
      set(
        (state) => {
          if (idx < 0 || !state.ipqc.schemas.handovers.length) {
            state.ipqc.currHandoverSessionId = ""
            state.ipqc.currHandoverIdx = -1

            return state
          }

          const handover = state.ipqc.schemas.handovers[idx]
          if (!handover) {
            state.ipqc.currHandoverSessionId = ""
            state.ipqc.currHandoverIdx = -1
            return state
          }

          state.ipqc.currHandoverIdx = idx
          state.ipqc.currHandoverSessionId = handover.handoverFromEmployeeSessionId

          return state
        },
        undefined,
        actionName("setCurrHandoverIdx")
      )
    },

    setSelfFormStatus(status) {
      set(
        (state) => {
          state.ipqc.formStatus.self = status

          return state
        },
        undefined,
        actionName("setSelfFormStatus")
      )
    },
    setHandoverFormStatus(sessionId, status) {
      set(
        (state) => {
          state.ipqc.formStatus.handovers[sessionId] = status

          return state
        },
        undefined,
        actionName("setHandoverFormStatus")
      )
    },

    setSelfFilled(filled) {
      set(
        (state) => {
          state.ipqc.filled.self = filled

          return state
        },
        undefined,
        "setSelfFilled"
      )
    },
    setHandoversFilled(data) {
      set(
        (state) => {
          state.ipqc.filled.handovers = data

          return state
        },
        undefined,
        "setHandoverFilled"
      )
    },
    removePendingSheets() {
      set(
        (state) => {
          const schemaProducts = state.ipqc.schemas.self.products

          state.ipqc.forms.self.products = state.ipqc.forms.self.products.map(
            (product, productIdx) => {
              const schemaProduct = schemaProducts[productIdx]
              product.productionLines = product.productionLines.filter(
                (__line, lineIdx) => {
                  const schemaLine = schemaProduct?.productionLines[lineIdx]
                  return (
                    this.getLineStatus({
                      productId: schemaProduct?.id || "",
                      lineId: schemaLine?.id || "",
                    }) === FormParamStatus.SUBMITTED
                  )
                }
              )

              return product
            }
          )

          state.ipqc.forms.self.products =
            state.ipqc.forms.self.products.filter(
              (product) => !!product.productionLines.length
            )

          state.ipqc.schemas.self.products =
            state.ipqc.schemas.self.products.map((product) => {
              product.productionLines = product.productionLines.filter(
                (line) => {
                  return (
                    this.getLineStatus({
                      productId: product.id,
                      lineId: line.id,
                    }) === FormParamStatus.SUBMITTED
                  )
                }
              )

              return product
            })

          state.ipqc.schemas.self.products =
            state.ipqc.schemas.self.products.filter(
              (product) => !!product.productionLines.length
            )

          return state
        },
        undefined,
        "removePendingSheets"
      )
    },
    setReadOnlyMode(isReadOnly) {
      set(
        (state) => {
          state.ipqc.isReadOnly = isReadOnly

          return state
        },
        undefined,
        actionName("setReadOnlyMode")
      )
    },
    setOfflineBundle(bundle) {
      set(
        (state) => {
          state.ipqc.offline.bundle = bundle

          if (bundle?.products?.length) {
            state.ipqc.shift.products = bundle.products.map((product) => ({
              id: product.id,
              name: product.name,
            }))
          }

          return state
        },
        undefined,
        actionName("setOfflineBundle")
      )
    },
    setOfflineHydrated(hydrated) {
      set(
        (state) => {
          state.ipqc.offline.hydrated = hydrated

          return state
        },
        undefined,
        actionName("setOfflineHydrated")
      )
    },
    startOfflineSession(meta) {
      set(
        (state) => {
          state.ipqc.offline.activeSession = meta

          return state
        },
        undefined,
        actionName("startOfflineSession")
      )
    },
    updateOfflineSessionMeta(meta) {
      set(
        (state) => {
          if (!meta) {
            state.ipqc.offline.activeSession = null

            return state
          }

          state.ipqc.offline.activeSession = {
            ...(state.ipqc.offline.activeSession || ({} as OfflineIpqcSession["meta"])),
            ...meta,
          }

          return state
        },
        undefined,
        actionName("updateOfflineSessionMeta")
      )
    },
    hydrateOfflineSession(session) {
      set(
        (state) => {
          state.ipqc.schemas.self = session.schema
          state.ipqc.forms.self = session.form
          state.ipqc.schemas.handovers = session.handoverSchemas || []
          state.ipqc.forms.handovers = session.handoverForms || []
          state.ipqc.filled.handovers = (session.handoverSchemas || []).map(
            (handover) => ({
              sessionId: handover.handoverFromEmployeeSessionId,
              filled: {},
            })
          )
          state.ipqc.lineStatus = session.lineStatus
          state.ipqc.currProductIdx = session.currProductIdx
          state.ipqc.currLineIdx = session.currLineIdx
          state.ipqc.currHandoverIdx = session.currHandoverIdx
          state.ipqc.currHandoverSessionId = session.currHandoverSessionId
          state.ipqc.offline.activeSession = session.meta
          state.ipqc.shift.processId = session.meta.processId
          state.ipqc.shift.shiftId = session.meta.shiftId

          if (!state.ipqc.shift.products.length) {
            state.ipqc.shift.products = session.schema.products.map((product) => ({
              id: product.id,
              name: product.name,
            }))
          }

          return state
        },
        undefined,
        actionName("hydrateOfflineSession")
      )
    },
    clearOfflineSession() {
      set(
        (state) => {
          state.ipqc.offline.activeSession = null

          return state
        },
        undefined,
        actionName("clearOfflineSession")
      )
    },
  },
})

type LineStatusKeyParams = {
  productId: string
  lineId: string
  sessionId?: string
}

export function toLineStatusKey(params: LineStatusKeyParams) {
  return `${params.productId}::${params.lineId}::${params.sessionId || "self"}`
}

export function toLineStatusKeyParts(key: string): {
  productId: string
  lineId: string
  sessionId: string
} {
  const parts = key.split("::")

  return {
    productId: parts.at(0) || "",
    lineId: parts.at(1) || "",
    sessionId: parts.at(2) || "",
  }
}

export default createIPQCSlice
